import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { AuthLoginMode, DataService, UserAvailabilityData, UserData, UserDataRealm, UserDataRole, UserProfileData, UserProfileNotificationChannelsData, UserProfileNotificationData } from './data.service';
import { LocalizationService } from './localization.service';
import { OptionMapsService } from './option-maps.service';
import { PersistenceService } from './persistence.service';
import { TimetableData, TimetableDataList } from '../models/timetable.model';
import { EnvironmentService, RealmData } from './environment.service';
import { PGUtilities } from '../utilities/pg-utilities';

export class AuthUser {
    id:string = null;
    name:string = null;
    surname:string = null;

    availableRealms:Array<UserDataRealm> = null;
    pendingRealms:Array<UserDataRealm> = null;
    unavailableRealms:Array<UserDataRealm> = null;
    nullRealm:UserDataRealm = null;
    realm:UserDataRealm = null;
    availableRoles:Array<UserDataRole> = null;
    role:UserDataRole = null;
    superadmin:boolean = null;
    email:string = null;
    telephone:string = null;
    codice_fiscale:string = null;

    /** ISO datetime of last acceptance, or null if never accepted */
    privacy_policy:string|null = null;
    /** ISO datetime of last acceptance, or null if never accepted */
    terms_and_conditions:string|null = null;

    stripeId:string = null;

    language:string = null;

    groups:Array<{
        id: string,
        label: string
    }> = [];

    getSelectableRealms() {
        let _retVal:Array<UserDataRealm> = [];

        for(let _realm of this.availableRealms) {
            _retVal.push(_realm)
        }

        for(let _realm of this.pendingRealms) {
            _retVal.push(_realm)
        }

        return _retVal;
    }

    getAllRealms() {
        let _retVal = this.getSelectableRealms()
        
        for(let _realm of this.unavailableRealms) {
            _retVal.push(_realm)
        }

        if(!this.superadmin) _retVal.push(this.nullRealm)

        return _retVal;
    }

    /**
     * Pivot acceptance dates come as MySQL "YYYY-MM-DD HH:mm:ss" (UTC, no offset),
     * while Content.updated_at is ISO with Z. `new Date(mysql)` is parsed as local time
     * and can make a fresh acceptance look older than the content by the TZ offset,
     * so the accept modal keeps reopening.
     */
    private _parseApiTimestamp(value:string):number {
        let _normalized = value.trim();

        // Bare datetime (space or T, optional fraction, no offset) → treat as UTC
        if(/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(_normalized)) {
            _normalized = _normalized.replace(' ', 'T') + 'Z';
        }

        return new Date(_normalized).getTime();
    }

    private _isAcceptanceCurrent(acceptedAt:string|null|undefined, contentUpdatedAt?:string|null) {
        if(acceptedAt == null || acceptedAt === '' || acceptedAt === '0') return false;

        // Legacy 0|1 flags are no longer valid once acceptance is date-based
        if(acceptedAt === '1' || (acceptedAt as any) === 1) return false;

        let _acceptedTime = this._parseApiTimestamp(acceptedAt);
        if(isNaN(_acceptedTime)) return false;

        if(contentUpdatedAt == null || contentUpdatedAt === '') return true;

        let _contentTime = this._parseApiTimestamp(contentUpdatedAt);
        if(isNaN(_contentTime)) return true;

        return _acceptedTime >= _contentTime;
    }

    getPrivacyPolicyAcceptedAt(realm:string):string|null {
        for(let _realm of this.getSelectableRealms()) {
            if(_realm.id == realm) {
                return _realm.pivot.privacy_policy;
            }
        }
        return null;
    }

    getTermsAndConditionsAcceptedAt(realm:string):string|null {
        for(let _realm of this.getSelectableRealms()) {
            if(_realm.id == realm) {
                return _realm.pivot.terms_and_conditions;
            }
        }
        return null;
    }

    hasAcceptedPrivacyPolicy(realm:string, contentUpdatedAt?:string|null) {
        return this._isAcceptanceCurrent(this.getPrivacyPolicyAcceptedAt(realm), contentUpdatedAt);
    }

    hasAcceptedTermsAndConditions(realm:string, contentUpdatedAt?:string|null) {
        return this._isAcceptanceCurrent(this.getTermsAndConditionsAcceptedAt(realm), contentUpdatedAt);
    }

    getCompleteName() {
        let _result = '';

        for(let _prop of ['name','surname']) {
            let _value = this[_prop];

            if(_value) {
                if(_result != '') _result += ' '
                _result += _value
            }
        }

        return _result
    }

    constructor(nullRealm:RealmData, fromData?:any) {
        if(fromData != null) {
            this.id = fromData.id;
            this.name = fromData.name;
            this.surname = fromData.surname;
            this.superadmin = fromData.superadmin == 1;
            this.email = fromData.email;
            this.telephone = fromData.telephone;
            this.codice_fiscale = fromData.codice_fiscale;

            if(this.name == this.email || this.name == this.telephone) this.name = null;

            this.language = fromData.language;

            this.privacy_policy = fromData.privacy_policy;
            this.terms_and_conditions = fromData.terms_and_conditions;

            this.availableRealms = [];
            this.pendingRealms = [];
            this.unavailableRealms = [];

            this.nullRealm = new UserDataRealm(nullRealm);
            this.nullRealm.id = null;

            if(this.superadmin) {
                this.availableRealms.push(this.nullRealm)
            }
 
            for(let _realm of fromData.related_realm) {
                let _realmObj = new UserDataRealm(_realm);

                if(_realm.pivot.status == 'accepted') {
                    this.availableRealms.push(_realmObj)
                }
                else if(_realm.pivot.status == 'rejected') {
                    this.unavailableRealms.push(_realmObj)
                }
                else {
                    this.pendingRealms.push(_realmObj)
                }
            }

            if(this.getSelectableRealms().length == 0) {
                this.availableRealms.push(this.nullRealm)
            }

            this.stripeId = fromData.stripe_id

            this.groups = fromData.related_group

            this.groups.sort((a, b) => {
                if(a.id < b.id) return -1;
                else if(a.id > b.id) return 1;
                else return 0;
            })

            this.realm = null;

            for(let _realm of this.getAllRealms()) {
                if(_realm.id == fromData.current_realm) {
                    this.realm = _realm
                    break;
                }
            }

            this.availableRoles = [];

            let _baseIn = false;
            
            for(let _role of fromData.related_role) {
                let _realmId = null;
                if(this.realm != null) _realmId = this.realm.id;

                if(_role.pivot.realm_id == _realmId) {
                    if(_role.name == 'base') _baseIn = true;
                    this.availableRoles.push(_role)
                }
            }

            if(!_baseIn) { // NB: in alcuni casi gli utenti non hanno base associato per i realm? è un po' strano e sarebbe da verificare BE
                for(let _role of fromData.related_role) {
                    if(_role.name == 'base' && _role.pivot.realm_id == null) { // ci butto dentro il base di realm null che comunque dovrebbe essere ok, mi serve prendere l'id del role
                        this.availableRoles.unshift(_role)
                        break;
                    }
                }
            }

            this.role = null;

            if(this.availableRoles != null) {
                if(fromData.current_role != null) {
                    for(let _cRole of this.availableRoles) {
                        if(_cRole.id == fromData.current_role) {
                            this.role = _cRole
                            break;
                        }
                    }
                }
            }
        }
    }
}

export class AuthAvailability {
    slots:TimetableDataList = new TimetableDataList();
    channels:Array<string> = [];

    constructor(fromData?:UserAvailabilityData) {
        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    if(i == 'slots') {
                        this[i] = new TimetableDataList(fromData[i])
                    }
                    else {
                        this[i] = PGUtilities.tryParseJSON(fromData[i])
                    }
                }
            }
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {    
    constructor(private environmentService:EnvironmentService, private dataService:DataService, private persistenceService:PersistenceService, private optionMapsService:OptionMapsService, private localizationService:LocalizationService) {}

    statusChange = new Subject<void>();
    userChange = new Subject<void>();
    profileChange = new Subject<void>();
    availabilityChange = new Subject<void>();

    afterLogin = new Subject<void>();
    beforeLogout = new Subject<void>();

    user:AuthUser = null;
    availability:AuthAvailability = null;
    profile:UserProfileData = null;

    isLoading = false;

    private _loginMode:AuthLoginMode;
    private _authToken:string;
    private _refreshToken:string;

    private async _handleStatusChange() {
        if(this.user != null) {
            let _setLanguage = null;

            if(this.user.language != null) {
                if(this.localizationService.currentLanguage != this.user.language) {
                    _setLanguage = this.user.language;
                }            
            }
            else {
                try {
                    await this._saveUserLanguage()
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService._saveUserLanguage error')
                }
            }

            if(!this.user.superadmin && this.localizationService.disableTranslations) {
                try {
                    this.localizationService.setDisableTranslations(false, _setLanguage != null)
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService.setDisableTranslations error')
                }
            }

            if(_setLanguage != null) {
                try {
                    await this.localizationService.setLanguage(_setLanguage)
                }
                catch(ex) {
                    console.log('AuthService._handleStatusChange localizationService.setLanguage error')
                }
            }

            this.isLoading = false;
            this.isSettingUserRealm = false;
            this.statusChange.next()
            this.afterLogin.next();
        }
        else {
            if(this.localizationService.disableTranslations) this.localizationService.setDisableTranslations(false)
            this.isLoading = false;
            this.isSettingUserRealm = false;
            this.statusChange.next()
        }
    }

    private _saveUserLanguage() {
        return new Promise<void>((resolve, reject) => {
            this.user.language = this.localizationService.currentLanguage;

            this.dataService.setUserData({ language: this.localizationService.currentLanguage }).subscribe((data) => {
                resolve()
            }, () => {
                resolve()
            })
        })
    }

    private _isAuthTokenExpired(token:string) {
        let _expires = this._getAuthTokenExpires(token);

        if(_expires == null) return true;
        else return _expires < Date.now()
    }

    private _getAuthTokenExpires(token:string) {
        try {
            let _payload = this._getTokenPayload(token);
            return _payload.exp * 1000;
        }
        catch(ex) {
            return null;
        }
    }

    private _getTokenPayload(token:string) {
        return JSON.parse(atob(token.split('.')[1]));
    }

    isLoggedIn(justUser?:boolean) {
        if(this.isLoading || this.user == null) return false;
        else {
            if(justUser) return true
            else {
                let _realmId = this.user.realm?.id;

                if(this.isSettingUserRealm) return false;
                else if(this.environmentService.environment.ForceRealm != null && _realmId != this.environmentService.environment.ForceRealm) return false;  
                else {
                    // null realm / skipAcceptConditions: skip privacy/terms acceptance
                    if(!this.skipAcceptConditions && _realmId != null && (!this.hasAcceptedPrivacyPolicy(_realmId) || !this.hasAcceptedTermsAndConditions(_realmId))) return false;
                    else if(this.user.pendingRealms.indexOf(this.user.realm) != -1 && this.user.realm.configuration.denyPendingAccess) return false;
                    else return true;
                }        
            }
        }
    }

    accessMode:'business'|'editor' = null;
    skipAcceptConditions = false;

    init(mode?:'business'|'editor', skipAcceptConditions?:boolean):Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.accessMode = mode;
            this.skipAcceptConditions = !!skipAcceptConditions;

            this.persistenceService.consentDataChange.subscribe(() => {
                if(this._loginMode != null) {
                    this.persistenceService.setItem('POIGEST_LoginMode', this._loginMode, 'technical');
                }

                if(this._authToken != null) {
                    this.persistenceService.setItem('POIGEST_AuthToken', this._authToken, 'technical');
                }

                if(this._refreshToken != null) {
                    this.persistenceService.setItem('POIGEST_RefreshToken', this._refreshToken, 'technical');
                }
            })

            this.localizationService.currentLanguageChange.subscribe(() => {
                if(this.user != null) {
                    this._saveUserLanguage();
                }
            })

            let _loginTokenUrlRegExp = /login_token=([^&]*)/

            if(_loginTokenUrlRegExp.test(window.location.search)) {
                let _loginToken = window.location.search.match(_loginTokenUrlRegExp)[1]

                console.log('AuthService.init: found login token in query string')
    
                let _fragment = window.location.hash
                let _cleanURL = window.location.href.replace(/#.*$/, '')
                    .replace(_loginTokenUrlRegExp, '')
                    .replace(/\&+$/, '').replace(/\?$/, '')
                
                window.history.replaceState({}, document.title, 
                    _cleanURL + _fragment);

                this.loginToken(_loginToken).then(() => {
                    resolve();
                })
            }
            else {
                let _savedLoginMode:AuthLoginMode = null;
                let _savedAuthToken:string = null;
                let _savedRefreshToken:string = null;
                
                let _accessTokenUrlRegExp = /access_token=([^&]*)/;
                
                if(_accessTokenUrlRegExp.test(window.location.search)) {
                    _savedLoginMode = 'default'
    
                    _savedAuthToken = window.location.search.match(_accessTokenUrlRegExp)[1]
    
                    _savedRefreshToken = null;
    
                    let _refreshTokenUrlRegExp = /refresh_token=([^&]*)/;
                    let _refreshTokenMatch = window.location.search.match(_refreshTokenUrlRegExp)
    
                    if(_refreshTokenMatch != null) _savedRefreshToken = _refreshTokenMatch[1]
    
                    if(_savedAuthToken != null) {
                        console.log('AuthService.init: found auth token in query string')
    
                        let _fragment = window.location.hash
                        let _cleanURL = window.location.href.replace(/#.*$/, '')
                            .replace(_accessTokenUrlRegExp, '').replace(_refreshTokenUrlRegExp, '')
                            .replace(/token_type=[^&]*/, '').replace(/expires_at=[^&]*/, '')
                            .replace(/\&+$/, '').replace(/\?$/, '')
                        
                        window.history.replaceState({}, document.title, 
                            _cleanURL + _fragment);
                    }
                }
                else {
                    _savedLoginMode = this.persistenceService.getItem('POIGEST_LoginMode') as AuthLoginMode;
                    _savedAuthToken = this.persistenceService.getItem('POIGEST_AuthToken');
                    _savedRefreshToken = this.persistenceService.getItem('POIGEST_RefreshToken');
    
                    if(_savedLoginMode != null) {
                        console.log('AuthService.init: found saved LoginMode')
                    }
    
                    if(_savedAuthToken != null) {
                        console.log('AuthService.init: found saved AuthToken')
                    }
    
                    if(_savedRefreshToken != null) {
                        console.log('AuthService.init: found saved RefreshToken')
                    }
                }
    
                if(_savedAuthToken != null) {
                    this._setAuthToken(_savedLoginMode, _savedAuthToken, _savedRefreshToken);

                    if(this._isAuthTokenExpired(_savedAuthToken)) {
                        console.log('AuthService.init: auth token is expired')
    
                        this.tryRefreshToken().then(()=> {
                            this._loadUserData().then(() => {
                                this._handleStatusChange().then(() => {
                                    resolve();
                                })
                            }, () => {
                                this.clearAuthToken();
                                this._handleStatusChange().then(() => {
                                    resolve();
                                })
                            })
                        })
                    }
                    else {
                        console.log('AuthService.init: auth token is valid')
                        console.log('Will expire in ' + this.localizationService.format.duration(this._getAuthTokenExpires(_savedAuthToken) - Date.now(), true, true))

                        this._loadUserData().then(() => {
                            this._handleStatusChange().then(() => {
                                resolve();
                            })
                        }, () => {
                            this.clearAuthToken();
                            this._handleStatusChange().then(() => {
                                resolve();
                            })
                        })
                    }
                }
                else {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                }
            }
        })
    }

    requestOTP(contact:string, type:'email'|'phone') {
        return new Promise<void>((resolve, reject) => {
            this.dataService.requestOTP(contact, type).subscribe(() => {
                resolve();
            }, () => {
                reject();
            })
        })
    }

    loginOTP(contact:string, type:'email'|'phone', password:string) {
        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            this.dataService.doLoginOTP(contact, type, password).subscribe((data:any) => {
                this._setAuthToken('otp', data.access_token, data.refresh_token)

                this._loadUserData().then(() => {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                })
            }, (error:any) => {
                reject();
            })
        })
    }

    loginCode(code:string) {
        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            this.dataService.doLoginCode(code).subscribe((data:any) => {
                this._setAuthToken('code', data.access_token, data.refresh_token)

                this._loadUserData().then(() => {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                })
            }, (error:any) => {
                reject();
            })
        })
    }

    loginToken(token:string) {
        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            this.dataService.doLoginToken(token).subscribe((data:any) => {
                this._setAuthToken('token', data.access_token, data.refresh_token)

                this._loadUserData().then(() => {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                })
            }, (error:any) => {
                reject();
            })
        })
    }
    
    login(email:string, password:string): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            this.dataService.doLogin(email, password).subscribe((data:any) => {
                this._setAuthToken('default', data.access_token, data.refresh_token)

                this._loadUserData().then(() => {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                })
            }, (error:any) => {
                reject();
            })
        })
    }

    hasAcceptedPrivacyPolicy(realmId:string) {
        if(realmId == null) return true; // null realm: skip
        if(this.user == null) return false;
        return this.user.hasAcceptedPrivacyPolicy(realmId, this.localizationService.getPrivacyPolicyUpdatedAt(realmId));
    }

    hasAcceptedTermsAndConditions(realmId:string) {
        if(realmId == null) return true; // null realm: skip
        if(this.user == null) return false;
        return this.user.hasAcceptedTermsAndConditions(realmId, this.localizationService.getTermsAndConditionsUpdatedAt(realmId));
    }

    accept(privacyPolicy:boolean, termsAndConditions:boolean, realmId:string): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            // Backend stores acceptance as the current datetime when true
            let _data:any = {
                privacy_policy: privacyPolicy,
                terms_and_conditions: termsAndConditions
            }

            this.isLoading = true;
            this.dataService.userAccept(_data, realmId).subscribe((data:any) => {
                this._loadUserData().then(() => {
                    this._handleStatusChange().then(() => {
                        resolve();
                    })
                })
            }, (error:any) => {
                reject();
            })
        })
    }

    logout(): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.beforeLogout.next();
            this.dataService.doLogout().subscribe(() => {
                this.user = null;
                this.availability = null;
                this.profile = null;
                this.clearAuthToken();
                
                this._handleStatusChange().then(() => {
                    resolve();
                })
            });
        })
    }

    clearAuthToken() {
        this._authToken = null;
        this._refreshToken = null;
        this.persistenceService.removeItem('POIGEST_LoginMode');
        this.persistenceService.removeItem('POIGEST_AuthToken');
        this.persistenceService.removeItem('POIGEST_RefreshToken');
    }

    hasAuthToken() {
        return (this.persistenceService.getItem('POIGEST_AuthToken') || this._authToken) ? true : false;
    }

    async getAuthToken(ignoreLock?:boolean) {
        if(ignoreLock) {
            console.log('Ignoring auth token lock')
        }
        else {
            let _isLocked = true;

            for(let i = 0; i < 5; i++) {
                _isLocked = this.persistenceService.getItem('POIGEST_LOCKAUTHTOKEN') != null
    
                if(_isLocked) {
                    console.log('Auth token locked, retrying (' + (i + 1) + ')')
                    await new Promise<void>((resolve) => {
                        setTimeout(() => {
                            resolve();
                        }, 2000)
                    })
                }
                else break;
            }
    
            if(_isLocked) {
                console.log('Auth token lock timeout, removing');
                this.persistenceService.removeItem('POIGEST_LOCKAUTHTOKEN')
            }
        }

        return this.persistenceService.getItem('POIGEST_AuthToken') || this._authToken;
    }

    private _getLoginMode() {
        return this.persistenceService.getItem('POIGEST_LoginMode') as AuthLoginMode || this._loginMode;
    }

    private _getRefreshToken() {
        return this.persistenceService.getItem('POIGEST_RefreshToken') || this._refreshToken;
    }

    private _setAuthToken(loginMode:AuthLoginMode, authToken:string, refreshToken:string) {
        this._authToken = authToken;
        this._refreshToken = refreshToken;

        this.persistenceService.setItem('POIGEST_LoginMode', loginMode, 'technical');
        this.persistenceService.setItem('POIGEST_AuthToken', authToken, 'technical');
        this.persistenceService.setItem('POIGEST_RefreshToken', refreshToken, 'technical');
    }

    async tryRefreshToken() {
        console.log('Trying to refresh token')

        if(this._getRefreshToken() == null) {
            console.log('No refresh token found')
            this.clearAuthToken();
            window.location.reload();
            throw new Error('No refresh token found')
        }
        else {
            let _isLocked = this.persistenceService.getItem('POIGEST_LOCKAUTHTOKEN');

            if(_isLocked != null) {
                console.log('Token is locked, waiting')

                this._authToken =  await this.getAuthToken();
                this._loginMode =  this._getLoginMode();
                this._refreshToken = this._getRefreshToken();
            }
            else {
                console.log('Locking token')
                this.persistenceService.setItem('POIGEST_LOCKAUTHTOKEN', 'true', 'technical');

                let _mode = this._getLoginMode()

                await new Promise<void>((resolve, reject) => {
                    this.dataService.refreshToken(_mode, this._getRefreshToken()).subscribe((data) => {
                        console.log('Unlocking token')
                        this.persistenceService.removeItem('POIGEST_LOCKAUTHTOKEN');
    
                        if(data.error != null) {
                            console.log('Token refresh error')
                            console.log(data.error)
    
                            console.log('Token refresh failed, reloading')
                            this.clearAuthToken();
                            window.location.reload();
                            reject()
                        }
                        else {
                            console.log('Token refresh successful')
                            this._setAuthToken(_mode, data.access_token, data.refresh_token)

                            resolve();
                        }
                    }, () => {
                        console.log('Unlocking token')
                        this.persistenceService.removeItem('POIGEST_LOCKAUTHTOKEN');
    
                        console.log('Token refresh failed, reloading')
                        this.clearAuthToken();
                        window.location.reload();
                        reject()
                    })
                })
            }
        }
    }

    isSuperAdmin():boolean {
        return this.isLoggedIn(true) && this.user.superadmin;
    }

    private _checkSetHighestRole() {
        return new Promise<void>((resolve) => {
            let _setRole:UserDataRole = null;

            if(this.accessMode) {
                for(let _role of this.user.availableRoles) {
                    if(_role.name == this.accessMode) {
                        if(this.user.role?.name != this.accessMode) {
                            _setRole = _role;
                            break;
                        }
                    }
                }
            }
            else if(this.user.role == null || this.user.role.name == 'base') {
                for(let _role of this.user.availableRoles) {
                    if(_setRole == null || _role.name != 'base') {
                        _setRole = _role;
                        if(_role.name == 'admin') break;
                    }
                }
            }

            if(_setRole != null) {
                this.setUserRole(_setRole.id).then(() => {
                    resolve()
                }, () => {
                    resolve()
                })
            }
            else {
                resolve();
            }
        })
    }

    hasAccessModeRole() {
        if(this.accessMode == null || this.user?.role?.name == this.accessMode) return true;
        else return false;
    }

    private _loadUserData():Promise<any> {
        return new Promise<void>((resolve, reject) => { 
            this.optionMapsService.setFixedOptionMap('*', 'group_id', null)
            this.optionMapsService.setFixedOptionMap('*', 'realm_id', null)
            this.optionMapsService.setFixedOptionMap('*', 'system_tags', null)

            this.dataService.getResourceData('SystemTag', { limit: 5000 }).subscribe((data) => { // TODO: questa roba ha senso che stia qua invece di essere caricata una tantum allo start dell'appliocazione?
                let _systemTags:Array<{ value: string, text: string, data: any }> = []

                for(let _item of data) {
                    _systemTags.push({
                        value: _item.id,
                        text: _item.label,
                        data: _item
                    })

                    this.localizationService.addTranslation('*', 'OPTIONMAPS.*.system_tags.' + _item.id, _item.label)
                }

                this.optionMapsService.setFixedOptionMap('*', 'system_tags', _systemTags)

                this.dataService.getUserData().subscribe((data) => {
                    this.user = null;
                    this.availability = null;
                    this.profile = null;
    
                    if(data == null) {
                        this.optionMapsService.setFixedOptionMap('*', 'group_id', [])
                        this.optionMapsService.setFixedOptionMap('*', 'realm_id', [])
                        this.localizationService.setAdditionalLanguages(null)
                        reject();
                    }
                    else {
                        this.user = new AuthUser(this.environmentService.nullRealm, data);

                        let _selectableRealms = this.user.getSelectableRealms()

                        if(this.user.realm == null && _selectableRealms.length > 0) {
                            this.setUserRealm(_selectableRealms[0].id).then(() => {
                                window.location.reload()
                            })
                        }
                        else if(this.user.realm != null && _selectableRealms.indexOf(this.user.realm) == -1) {
                            this.setUserRealm(_selectableRealms[0]?.id).then(() => {
                                window.location.reload()
                            })
                        }
                        else {
                            let _groupOptions = []
            
                            if(this.user.groups != null) {
                                for(let _group of this.user.groups) {
                                    _groupOptions.push({ value: _group.id, text: _group.label })
                                }
                            }
            
                            this.optionMapsService.setFixedOptionMap('*', 'group_id', _groupOptions)
        
                            let _realmOptions = []
            
                            for(let _realm of _selectableRealms) {
                                if(_realm.id != null) _realmOptions.push({ value: _realm.id, text: _realm.name })
                            }
            
                            this.optionMapsService.setFixedOptionMap('*', 'realm_id', _realmOptions)
                            this.localizationService.setAdditionalLanguages(this.user.realm.additional_languages)

                            this.dataService.getUserAvailability().subscribe((data) => {
                                this.availability = new AuthAvailability(data);
        
                                this.dataService.getUserProfile().subscribe((data) => {
                                    if(data == null) {
                                        this.profile = {
                                            id: null,
                                            user_id: this.user.id,
                                            languages: this.environmentService.environment.AvailableApplicationLanguages[0],
                                            customer_care: false,
                                            company: null,
                                            vat_number: null,
                                            notification_settings: null
                                        }
                                    }
                                    else {
                                        this.profile = data;
                                    }

                                    this._checkSetHighestRole().then(() => {
                                        resolve()
                                    })
                                })
                            })
                        }
                    }
                })
            })
        })
    }

    requestAddRealm(id:string) {
        return new Promise<void>((resolve, reject) => {
            let _observable:Observable<any> = null;

            if(this.accessMode) {
                _observable = this.dataService.postRequest('/me/userRealm/requestRole', { realm_id: id, role: this.accessMode })
            }
            else {
                _observable = this.dataService.postRequest('/realm/request/' + id, null)
            }

            _observable.subscribe((data) => {
                if(data != null) {
                    let _realm = JSON.parse(JSON.stringify(this.environmentService.getRealm(id)));
                    _realm.pivot = data;

                    this.user.pendingRealms.push(_realm)
                    this.statusChange.next();
                    resolve()
                }
                else {
                    reject()
                }
            })
        })
    }

    isSettingUserRealm = false;

    setUserRealm(id:string, force?:boolean) {
        return new Promise<void>((resolve, reject) => {
            let _realm:UserDataRealm = null;

            for(let _selectable of this.user.getSelectableRealms()) {
                if(_selectable.id == id) {
                    _realm = _selectable;
    
                    break;
                }
            }

            if(force || _realm != null) {
                this.isSettingUserRealm = true;

                this.dataService.setUserData({
                    current_realm: id
                }).subscribe((data) => {
                    this._loadUserData().then(() => {
                        this._checkSetHighestRole().then(() => {
                            this._handleStatusChange().then(() => {
                                resolve();
                            })
                        })
                    }, () => {
                        this.isSettingUserRealm = false;
                        reject()
                    })
                }, () => {
                    this.isSettingUserRealm = false;
                    reject()
                })
            }
            else {
                this.isSettingUserRealm = false;
                reject();
            }
        }) 
    }

    setUserRole(id:string) {
        return new Promise<void>((resolve, reject) => {
            let _role:UserDataRole = null;

            for(let _cRole of this.user.availableRoles) {
                if(_cRole.id == id) {
                    _role = _cRole;
    
                    break;
                }
            }

            if(_role != null) {
                this.dataService.setUserData({
                    current_role: _role.id
                }).subscribe((data) => {
                    if(data != null) {
                        this.user.role = _role;
                        this.statusChange.next();
    
                        resolve()
                    }
                    else {
                        reject()
                    }
                }, () => {
                    reject()
                })
            }
            else {
                reject();
            }
        })
    }

    changePassword(password:string) {
        return new Promise<UserData>((resolve, reject) => {
            if(this.isLoggedIn(true)) {
                this.dataService.setUserData({ 
                    password: password 
                }).subscribe((data) => {
                    resolve(data)
                }, () => {
                    reject();   
                })
            }
            else {
                reject();
            }
        })
    }

    setUserAvailabilitySlots(slots: Array<TimetableData>) {
        return new Promise<UserAvailabilityData>((resolve, reject) => {
            if(this.isLoggedIn()) {
                this.dataService.setUserAvailabilitySlots(slots).subscribe((data) => {
                    this.availability = new AuthAvailability(data);
                    this.availabilityChange.next();
                    resolve(data)
                }, () => {
                    reject();   
                })
            }
            else {
                reject();
            }
        })
    }

    setUserAvailabilityChannels(channels:string) {
        return new Promise<UserAvailabilityData>((resolve, reject) => {
            if(this.isLoggedIn()) {
                this.dataService.setUserAvailabilityChannels(channels).subscribe((data) => {
                    this.availability = new AuthAvailability(data);
                    this.availabilityChange.next();
                    resolve(data)
                }, () => {
                    reject();   
                })
            }
            else {
                reject();
            }
        })
    }


    setUserData(user:Partial<UserData>) {
        return new Promise<UserData>((resolve, reject) => {
            if(this.isLoggedIn()) {
                this.dataService.setUserData(user).subscribe((data) => {
                    if(data == null) reject();
                    else {
                         for(let i in data) {
                            this.user[i] = data[i]
                        }
                        this.profileChange.next();
                        resolve(data)
                    }
                }, (error) => {
                    reject(error);   
                })
            }
            else {
                reject();   
            }
        })
    }

    setUserProfile(profile:Partial<UserProfileData>) {
        return new Promise<UserProfileData>((resolve, reject) => {
            if(this.isLoggedIn()) {
                this.dataService.setUserProfile(profile).subscribe((data) => {
                    if(data == null) reject();
                    else {
                        for(let i in data) {
                            this.profile[i] = data[i]
                        }
                        this.profileChange.next();
                        resolve(data)
                    }
                }, () => {
                    reject();   
                })
            }
            else {
                reject();   
            }
        })
    }

    isUserProfileIncomplete() {
        return this.isLoggedIn(true) && this.user != null && this.profile != null && (
            (
                (this.user.email == null || this.user.email == '') &&
                (this.user.telephone == null || this.user.telephone == '')
            ) ||
            this.user.name == null || this.user.name == '' || 
            this.user.surname == null || this.user.surname == '')
    }

    isBusinessProfileIncomplete() {
        return this.isLoggedIn(true) && this.user != null && this.profile != null && (
            (
                (this.user.email == null || this.user.email == '') &&
                (this.user.telephone == null || this.user.telephone == '')
            ) ||
            this.user.name == null || this.user.name == '' || 
            this.user.surname == null || this.user.surname == '' || 
            this.profile?.company == null || this.profile.company == '' ||
            this.profile.vat_number == null || this.profile.vat_number == '')
    }

}